import React, { useMemo, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  useListMarktArticlesQuery,
  useListMarktStocksQuery,
} from '../../../redux/api/marktApi';
import { useCreateReservationMutation } from '../../../redux/api/reservationApi';
import { useApiErrorToast } from '../../../hooks/useApiError';

const EMPTY = {
  customer_name: '',
  customer_email: '',
  customer_phone: '',
  note: '',
  pickup_until: '',
};

const CreateReservationModal = ({ show, onHide }) => {
  const [form, setForm] = useState(EMPTY);
  const [items, setItems] = useState([]); // [{ article, name, quantity }]
  const [articleId, setArticleId] = useState('');
  const [qty, setQty] = useState(1);

  const apiErrorToast = useApiErrorToast();
  const { data: articles } = useListMarktArticlesQuery();
  const { data: stocks } = useListMarktStocksQuery();
  const [createReservation, { isLoading }] = useCreateReservationMutation();

  // articleId -> sellable quantity
  const sellableMap = useMemo(() => {
    const map = new Map();
    (stocks || []).forEach((s) => {
      if (s.article != null) map.set(s.article, s.quantity_sellable ?? 0);
    });
    return map;
  }, [stocks]);

  const setField = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const reset = () => {
    setForm(EMPTY);
    setItems([]);
    setArticleId('');
    setQty(1);
  };

  const close = () => {
    reset();
    onHide();
  };

  const addItem = () => {
    if (!articleId) return;
    const article = (articles || []).find((a) => a.id === articleId);
    if (!article) return;
    if (items.some((it) => it.article === articleId)) {
      toast.error('Artikel bereits hinzugefügt.');
      return;
    }
    const amount = parseInt(qty, 10);
    if (isNaN(amount) || amount < 1) {
      toast.error('Ungültige Menge.');
      return;
    }
    const sellable = sellableMap.get(articleId) ?? 0;
    if (amount > sellable) {
      toast.error(`Nur ${sellable} verfügbar (reservierbar).`);
      return;
    }
    setItems((prev) => [
      ...prev,
      { article: articleId, name: article.name, quantity: amount },
    ]);
    setArticleId('');
    setQty(1);
  };

  const removeItem = (id) =>
    setItems((prev) => prev.filter((it) => it.article !== id));

  const submit = async () => {
    if (!form.customer_name.trim()) {
      toast.error('Bitte einen Namen angeben.');
      return;
    }
    if (items.length === 0) {
      toast.error('Bitte mindestens einen Artikel hinzufügen.');
      return;
    }
    const payload = {
      customer_name: form.customer_name.trim(),
      customer_email: form.customer_email.trim(),
      customer_phone: form.customer_phone.trim(),
      note: form.note.trim(),
      pickup_until: form.pickup_until || null,
      items: items.map((it) => ({ article: it.article, quantity: it.quantity })),
    };
    try {
      await createReservation(payload).unwrap();
      toast.success('Reservierung angelegt (bestätigt).');
      close();
    } catch (err) {
      apiErrorToast(err, 'Anlegen fehlgeschlagen');
    }
  };

  return (
    <Modal show={show} onHide={close} size='lg' backdrop={'static'}>
      <Modal.Header closeButton>
        <Modal.Title>Neue Reservierung</Modal.Title>
      </Modal.Header>
      <Modal.Body className='text-start'>
        <div className='row g-2 mb-3'>
          <div className='col-md-6'>
            <label className='form-label'>Name *</label>
            <input
              className='form-control'
              value={form.customer_name}
              onChange={setField('customer_name')}
            />
          </div>
          <div className='col-md-6'>
            <label className='form-label'>E-Mail</label>
            <input
              type='email'
              className='form-control'
              value={form.customer_email}
              onChange={setField('customer_email')}
            />
          </div>
          <div className='col-md-6'>
            <label className='form-label'>Telefon</label>
            <input
              className='form-control'
              value={form.customer_phone}
              onChange={setField('customer_phone')}
            />
          </div>
          <div className='col-md-6'>
            <label className='form-label'>Abholung bis</label>
            <input
              type='date'
              className='form-control'
              value={form.pickup_until}
              onChange={setField('pickup_until')}
            />
          </div>
          <div className='col-12'>
            <label className='form-label'>Notiz</label>
            <input
              className='form-control'
              value={form.note}
              onChange={setField('note')}
            />
          </div>
        </div>

        <h6>Artikel</h6>
        <div className='input-group mb-2'>
          <select
            className='form-select'
            value={articleId}
            onChange={(e) => setArticleId(e.target.value)}
          >
            <option value=''>Artikel auswählen…</option>
            {(articles || []).map((a) => {
              const sellable = sellableMap.get(a.id) ?? 0;
              return (
                <option key={a.id} value={a.id} disabled={sellable <= 0}>
                  {a.name} ({sellable} verfügbar)
                </option>
              );
            })}
          </select>
          <input
            type='number'
            min='1'
            className='form-control'
            style={{ maxWidth: 90 }}
            value={qty}
            onChange={(e) => setQty(e.target.value)}
          />
          <Button variant='outline-primary' onClick={addItem}>
            <i className='fas fa-plus' />
          </Button>
        </div>

        {items.length > 0 && (
          <ul className='list-group mb-2'>
            {items.map((it) => (
              <li
                key={it.article}
                className='list-group-item d-flex justify-content-between align-items-center'
              >
                <span>
                  <strong>{it.quantity}×</strong> {it.name}
                </span>
                <button
                  className='btn btn-sm btn-outline-danger'
                  onClick={() => removeItem(it.article)}
                >
                  ❌
                </button>
              </li>
            ))}
          </ul>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={close}>
          Abbrechen
        </Button>
        <Button variant='success' onClick={submit} disabled={isLoading}>
          Reservierung anlegen
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateReservationModal;
